import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { formatTimeFromDate } from '../utils/timeFormat';
import './CreateEditEvent.css';

const CreateEditEvent = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [eventOwnerId, setEventOwnerId] = useState(null);
  // Тип события при загрузке: уже созданный открытый турнир редактируют и те,
  // кому создавать такие события нельзя
  const [loadedEventType, setLoadedEventType] = useState(null);
  // До загрузки события форма не рендерится: иначе на миг видны блоки,
  // которые скрыты для официальных событий и для соорганизаторов
  const [eventLoaded, setEventLoaded] = useState(!id);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_type: 'group_ride',
    status: 'custom',
    image: '',
    registration_closes_hours_before: null,
    races_per_stage: null,
    starts_at: '',
    ends_at: '',
    long_event: false,
    hidden: false,
    individual_competition: false,
    team_competition: false,
    max_scores: 1000,
    scores_step: 10,
    members_accounting: 3,
    max_team_members: null,
    wild_cards: null,
    creation_type: 'organizer_created',
    wild_card_description: '',
    template: '',
    manual_races: false,
    route_links: ['']
  });

  const [eventUrl, setEventUrl] = useState('');
  const [scrapingEvent, setScrapingEvent] = useState(false);

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [syncingRoutes, setSyncingRoutes] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(null);

  // Маршрут, который добавляют в состав открытого турнира
  const [stageRouteUrl, setStageRouteUrl] = useState('');
  const [addingStage, setAddingStage] = useState(false);

  // Событие на чужих гонках: гонки заводятся вручную — маршрут ROUVY, ссылка на
  // гонку, время старта (в местном времени браузера) и необязательные названия
  // маршрута и гонки.
  // При создании события это список строк, при редактировании — форма
  // добавления одной гонки
  const emptyManualRace = { route: '', route_name: '', event_url: '', name: '', start_at: '' };
  const [manualRaces, setManualRaces] = useState([emptyManualRace]);
  const [newManualRace, setNewManualRace] = useState(emptyManualRace);
  const [addingManualRace, setAddingManualRace] = useState(false);

  // Гонки, уже добавленные к событию — с возможностью удаления
  const [eventRoutes, setEventRoutes] = useState([]);
  const [loadingEventRoutes, setLoadingEventRoutes] = useState(false);
  const [deletingRaceId, setDeletingRaceId] = useState(null);

  // Соорганизаторы: могут добавлять гонки к событию, их проезды Rouvy тоже попадают в событие
  const [coOrganizers, setCoOrganizers] = useState([]);
  const [organizerSearch, setOrganizerSearch] = useState('');
  const [organizerResults, setOrganizerResults] = useState([]);
  const [searchingOrganizers, setSearchingOrganizers] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchEvent();
      fetchEventRoutes();
    }
  }, [id]);

  const fetchEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/events/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEventOwnerId(data.user_id);
        setLoadedEventType(data.event_type);
        setCoOrganizers(data.co_organizers || []);
        setFormData({
          name: data.name || '',
          description: data.description || '',
          event_type: data.event_type || 'group_ride',
          status: data.status || 'custom',
          image: data.image || '',
          registration_closes_hours_before: data.registration_closes_hours_before || null,
          races_per_stage: data.races_per_stage ?? null,
          starts_at: toDateTimeInput(data.starts_at),
          ends_at: toDateTimeInput(data.ends_at),
          long_event: data.long_event || false,
          hidden: data.hidden || false,
          individual_competition: data.individual_competition || false,
          team_competition: data.team_competition || false,
          max_scores: data.max_scores || 1000,
          scores_step: data.scores_step || 10,
          members_accounting: data.members_accounting || 3,
          max_team_members: data.max_team_members || null,
          wild_cards: data.wild_cards ?? null,
          creation_type: data.creation_type || 'organizer_created',
          wild_card_description: data.wild_card_description || '',
          template: data.template || '',
          manual_races: data.manual_races || false,
          route_links: data.routes.map(route => route.rouvy_link).filter(Boolean)
        });
        
        if (data.image) {
          setImagePreview(data.image);
        }
        
        if (formData.route_links.length === 0) {
          setFormData(prev => ({ ...prev, route_links: [''] }));
        }
      } else {
        setErrors([t('events.errors.fetch_failed')]);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      setErrors([t('events.errors.fetch_failed')]);
    } finally {
      setEventLoaded(true);
    }
  };

  // Гонки события: список доступен организатору и соорганизаторам
  const fetchEventRoutes = async () => {
    setLoadingEventRoutes(true);
    try {
      const response = await axios.get(`/api/events/${id}/event_routes`);
      setEventRoutes(response.data.event_routes || []);
    } catch (error) {
      console.error('Error fetching event routes:', error);
      setEventRoutes([]);
    } finally {
      setLoadingEventRoutes(false);
    }
  };

  const handleDeleteRace = async (raceId) => {
    if (!window.confirm(t('events.confirm_delete_race'))) {
      return;
    }

    setDeletingRaceId(raceId);
    setErrors([]);

    try {
      await axios.delete(`/api/events/${id}/event_routes/${raceId}`);
      setEventRoutes(prev => prev.filter(race => race.id !== raceId));
    } catch (error) {
      console.error('Error deleting event route:', error);
      setErrors([error.response?.data?.error || t('events.errors.delete_race_failed')]);
    } finally {
      setDeletingRaceId(null);
    }
  };

  // Состав открытого турнира — это просто набор маршрутов.
  // Времени проезда у них нет: гонку в ROUVY участник создает себе сам
  const handleAddStage = async (e) => {
    e.preventDefault();

    if (!stageRouteUrl.trim()) {
      setErrors([t('events.errors.route_url_required')]);
      return;
    }

    setAddingStage(true);
    setErrors([]);

    try {
      const response = await axios.post(`/api/events/${id}/event_routes`, {
        route_url: stageRouteUrl.trim()
      });

      setEventRoutes(prev => [...prev, response.data.event_route]);
      setStageRouteUrl('');
    } catch (error) {
      console.error('Error adding stage:', error);
      const data = error.response?.data;
      setErrors(data?.errors || [data?.error || t('events.errors.add_stage_failed')]);
    } finally {
      setAddingStage(false);
    }
  };

  const handleAddManualRace = async () => {
    if (!isManualRaceComplete(newManualRace)) {
      setErrors([t('events.errors.manual_race_fields_required')]);
      return;
    }

    setAddingManualRace(true);
    setErrors([]);

    try {
      const response = await axios.post(`/api/events/${id}/event_routes`, manualRacePayload(newManualRace));

      setEventRoutes(prev => [...prev, response.data.event_route]);
      setNewManualRace(emptyManualRace);
    } catch (error) {
      console.error('Error adding manual race:', error);
      const data = error.response?.data;
      setErrors(data?.errors || [data?.error || t('events.errors.add_manual_race_failed')]);
    } finally {
      setAddingManualRace(false);
    }
  };

  // Время старта обязательно: ручные гонки бывают только у событий с расписанием
  const isManualRaceComplete = (race) => Boolean(race.route.trim() && race.event_url.trim() && race.start_at);

  // Время старта уходит на бэкенд в UTC, как и период открытого турнира
  const manualRacePayload = (race) => ({
    route: race.route.trim(),
    route_name: race.route_name.trim(),
    event_url: race.event_url.trim(),
    name: race.name.trim(),
    start_date_time_utc: fromDateTimeInput(race.start_at)
  });

  const updateManualRace = (index, field, value) => {
    setManualRaces(prev => prev.map((race, i) => (i === index ? { ...race, [field]: value } : race)));
  };

  const addManualRaceRow = () => {
    setManualRaces(prev => [...prev, emptyManualRace]);
  };

  const removeManualRaceRow = (index) => {
    setManualRaces(prev => (prev.length === 1 ? [emptyManualRace] : prev.filter((_, i) => i !== index)));
  };

  // Поле datetime-local работает в местном времени браузера, а на бэкенд период
  // уходит в UTC — переводим в обе стороны
  const toDateTimeInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const pad = (number) => number.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const fromDateTimeInput = (value) => (value ? new Date(value).toISOString() : null);

  const formatRaceDate = (dateTimeStr) => {
    if (!dateTimeStr) return '—';
    const date = new Date(dateTimeStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');

    return `${day}.${month}.${date.getFullYear()} ${formatTimeFromDate(date, language)}`;
  };

  // Поиск пользователей для добавления в соорганизаторы
  useEffect(() => {
    const query = organizerSearch.trim();
    if (query.length < 2) {
      setOrganizerResults([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearchingOrganizers(true);
      try {
        const response = await axios.get('/api/users', { params: { search: query } });
        if (!cancelled) {
          setOrganizerResults(response.data.users || []);
        }
      } catch (error) {
        console.error('Error searching users:', error);
        if (!cancelled) setOrganizerResults([]);
      } finally {
        if (!cancelled) setSearchingOrganizers(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [organizerSearch]);

  const addCoOrganizer = (candidate) => {
    setCoOrganizers(prev => (
      prev.some(organizer => organizer.id === candidate.id) ? prev : [...prev, candidate]
    ));
    setOrganizerSearch('');
    setOrganizerResults([]);
  };

  const removeCoOrganizer = (userId) => {
    setCoOrganizers(prev => prev.filter(organizer => organizer.id !== userId));
  };

  const organizerName = (organizer) => {
    const name = [organizer.first_name, organizer.last_name].filter(Boolean).join(' ').trim();
    return organizer.nickname ? `${name || organizer.nickname} (${organizer.nickname})` : name;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRouteChange = (index, value) => {
    const newRouteLinks = [...formData.route_links];
    newRouteLinks[index] = value;
    setFormData(prev => ({
      ...prev,
      route_links: newRouteLinks
    }));
  };

  const addRouteField = () => {
    // Для group_ride и race - только одна трасса
    if ((formData.event_type === 'group_ride' || formData.event_type === 'race') && formData.route_links.length >= 1) {
      setErrors([t('events.errors.single_route_only')]);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      route_links: [...prev.route_links, '']
    }));
  };

  const removeRouteField = (index) => {
    if (formData.route_links.length === 1) {
      return; // Keep at least one field
    }
    
    const newRouteLinks = formData.route_links.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      route_links: newRouteLinks
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors([t('profile.avatarTooLarge')]);
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.image;

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', imageFile);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/upload/event_image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data.path;
    } catch (error) {
      throw new Error(t('profile.avatarUploadError'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setLoading(true);

    // Validation
    if (!formData.name.trim()) {
      setErrors([t('events.errors.name_required')]);
      setLoading(false);
      return;
    }

    if (formData.event_type === 'open_tournament' && formData.starts_at && formData.ends_at &&
        new Date(formData.ends_at) <= new Date(formData.starts_at)) {
      setErrors([t('events.errors.period_order')]);
      setLoading(false);
      return;
    }

    // Check single route for group_ride and race
    const validRouteLinks = formData.route_links.filter(link => link.trim() !== '');
    if ((formData.event_type === 'group_ride' || formData.event_type === 'race') && validRouteLinks.length > 1) {
      setErrors([t('events.errors.single_route_only')]);
      setLoading(false);
      return;
    }

    // Гонки на чужих трассах уходят вместе с событием — бэкенд заводит их
    // в той же транзакции. Пустые строки пропускаем, недозаполненные не пускаем
    const races = !isEditMode && isManualRaces
      ? manualRaces
        .map(manualRacePayload)
        .filter(race => race.route || race.route_name || race.event_url || race.name || race.start_date_time_utc)
      : [];

    if (races.some(race => !race.route || !race.event_url || !race.start_date_time_utc)) {
      setErrors([t('events.errors.manual_race_fields_required')]);
      setLoading(false);
      return;
    }

    try {
      // Загружаем изображение, если выбрано новое
      let imagePath = formData.image;
      if (imageFile) {
        imagePath = await uploadImage();
      }

      const token = localStorage.getItem('token');
      const url = isEditMode ? `/api/events/${id}` : '/api/events';
      const method = isEditMode ? 'PUT' : 'POST';

      const submitData = {
        ...formData,
        image: imagePath,
        // Командные поля имеют смысл только при командном зачете:
        // с выключенным team_competition бэкенд отклоняет событие (см. Event#members_accounting_only_for_team_competition)
        members_accounting: formData.team_competition ? formData.members_accounting : null,
        max_team_members: formData.team_competition ? formData.max_team_members : null,
        // Период проведения есть только у открытого турнира — бэкенд отклонит
        // его у остальных типов (см. Event#period_only_for_open_tournament)
        starts_at: isOpenTournament ? fromDateTimeInput(formData.starts_at) : null,
        ends_at: isOpenTournament ? fromDateTimeInput(formData.ends_at) : null,
        // Ручные гонки бывают только у пользовательских событий с расписанием
        manual_races: isManualRaces,
        co_organizer_ids: coOrganizers.map(organizer => organizer.id)
      };

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ event: submitData, races })
      });

      if (response.ok) {
        // Маршруты синхронизируются с Rouvy на бэкенде при создании/обновлении события,
        // повторно их можно подтянуть кнопкой «Синхронизировать маршруты»
        navigate('/events');
      } else {
        const text = await response.text();
        let data = {};
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (e) {
            console.error('Error parsing error response:', e);
          }
        }
        setErrors(data.errors || [t('events.errors.save_failed')]);
      }
    } catch (error) {
      console.error('Error saving event:', error);
      setErrors([t('events.errors.save_failed')]);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncRoutes = async () => {
    if (!formData.name.trim()) {
      setErrors([t('events.errors.name_required_for_sync')]);
      return;
    }

    setSyncingRoutes(true);
    setErrors([]);
    setSyncSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/events/${id}/sync_routes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Синхронизация идет в фоне — маршруты появятся после обработки очереди
        setSyncSuccess(t('events.success.sync_routes_enqueued'));
      } else {
        const data = await response.json();
        setErrors([data.error || t('events.errors.sync_routes_failed')]);
      }
    } catch (error) {
      console.error('Error syncing routes:', error);
      setErrors([t('events.errors.sync_routes_failed')]);
    } finally {
      setSyncingRoutes(false);
    }
  };

  const handleScrapeEvent = async () => {
    if (!eventUrl.trim()) {
      setErrors([t('events.errors.event_url_required')]);
      return;
    }

    // Validate URL format
    if (!eventUrl.match(/^https?:\/\/riders\.rouvy\.com\/events\//)) {
      setErrors([t('events.errors.invalid_event_url')]);
      return;
    }

    setScrapingEvent(true);
    setErrors([]);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/rouvy_events/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ event_url: eventUrl })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Add scraped routes to route_links
        const scrapedLinks = data.routes.map(route => route.url).filter(Boolean);
        
        if (scrapedLinks.length === 0) {
          setErrors([t('events.errors.no_routes_found')]);
        } else {
          // For group_ride and race, take only first route
          if (formData.event_type === 'group_ride' || formData.event_type === 'race') {
            setFormData(prev => ({
              ...prev,
              route_links: [scrapedLinks[0]]
            }));
          } else {
            setFormData(prev => ({
              ...prev,
              route_links: scrapedLinks
            }));
          }
          
          // Clear event URL after successful scraping
          setEventUrl('');
        }
      } else {
        const data = await response.json();
        setErrors([data.error || t('events.errors.scrape_failed')]);
      }
    } catch (error) {
      console.error('Error scraping event:', error);
      setErrors([t('events.errors.scrape_failed')]);
    } finally {
      setScrapingEvent(false);
    }
  };

  const maxRoutes = (formData.event_type === 'group_ride' || formData.event_type === 'race') ? 1 : 100;

  // Соорганизатор может только добавлять гонки — настройки события ему недоступны
  const isOwner = !isEditMode || user?.admin || eventOwnerId === user?.id;
  const isCoOrganizerOnly = isEditMode && !isOwner && coOrganizers.some(organizer => organizer.id === user?.id);
  // У официальных событий маршруты ведет ROUVY: шаблон поиска, список гонок и соорганизаторы не нужны
  const isOfficialEvent = formData.status === 'official';
  // Открытый турнир: расписания гонок в ROUVY нет, этапы заводит
  // организатор — маршрут плюс окно, в которое его можно проехать
  const isOpenTournament = formData.event_type === 'open_tournament';
  // Событие на чужих гонках: синхронизации по шаблону нет, гонки заводятся вручную.
  // Способ выбирается при создании и потом не меняется
  const isManualRaces = formData.manual_races && !isOfficialEvent && !isOpenTournament;
  // Открытый турнир заводит только владелец проекта (users.open_tournament_access)
  const canChooseOpenTournament = Boolean(user?.open_tournament_access) || loadedEventType === 'open_tournament';

  // Поля ручной гонки: маршрут ROUVY (ID или ссылка), ссылка на гонку и время старта
  // обязательны, названия маршрута и гонки — если ROUVY их не отдает или нужны свои.
  // Пары идут рядом: маршрут и его название, гонка и ее название
  const manualRaceInputs = (race, onChange) => (
    <div className="manual-race-grid">
      <label className="manual-race-field">
        <span>{t('events.fields.manual_race_route')} *</span>
        <input
          type="text"
          value={race.route}
          onChange={(e) => onChange('route', e.target.value)}
          placeholder={t('events.placeholders.manual_race_route')}
        />
      </label>
      <label className="manual-race-field">
        <span>{t('events.fields.manual_race_route_name')}</span>
        <input
          type="text"
          value={race.route_name}
          onChange={(e) => onChange('route_name', e.target.value)}
          placeholder={t('events.placeholders.manual_race_name')}
        />
      </label>
      <label className="manual-race-field">
        <span>{t('events.fields.manual_race_event_url')} *</span>
        <input
          type="text"
          value={race.event_url}
          onChange={(e) => onChange('event_url', e.target.value)}
          placeholder={t('events.placeholders.manual_race_event_url')}
        />
      </label>
      <label className="manual-race-field">
        <span>{t('events.fields.manual_race_name')}</span>
        <input
          type="text"
          value={race.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder={t('events.placeholders.manual_race_name')}
        />
      </label>
      <label className="manual-race-field">
        <span>{t('events.fields.manual_race_start')} *</span>
        <input
          type="datetime-local"
          value={race.start_at}
          onChange={(e) => onChange('start_at', e.target.value)}
        />
      </label>
    </div>
  );

  // Создание события на чужих гонках: сразу задаем список гонок
  const manualRacesCreateSection = (
    <div className="form-group">
      <label>{t('events.fields.manual_races_list')}</label>
      <p className="field-hint" style={{ marginTop: '4px', marginBottom: '10px' }}>
        {t('events.hints.manual_races_list')}
      </p>

      {manualRaces.map((race, index) => (
        <div key={index} className="manual-race-card">
          {manualRaceInputs(race, (field, value) => updateManualRace(index, field, value))}
          <button
            type="button"
            className="added-race-delete"
            onClick={() => removeManualRaceRow(index)}
          >
            {t('common.remove')}
          </button>
        </div>
      ))}

      <button type="button" className="btn-add-route" onClick={addManualRaceRow}>
        {t('events.buttons.add_manual_race_row')}
      </button>
    </div>
  );

  // Добавление гонки в уже созданное событие на чужих гонках
  const manualRaceAddForm = (
    <div className="manual-race-card">
      {manualRaceInputs(newManualRace, (field, value) => setNewManualRace(prev => ({ ...prev, [field]: value })))}
      <button
        type="button"
        className="btn-add-stage"
        onClick={handleAddManualRace}
        disabled={addingManualRace || !isManualRaceComplete(newManualRace)}
      >
        {addingManualRace ? t('events.buttons.adding_manual_race') : t('events.buttons.add_manual_race')}
      </button>
    </div>
  );

  // Синхронизация маршрутов с Rouvy по шаблону — выполняется в фоне (Sidekiq)
  const routesSyncSection = (
    <div style={{
      border: '2px dashed #4CAF50',
      padding: '20px',
      borderRadius: '8px',
      backgroundColor: 'var(--c-f9fff9-bg)',
      marginTop: '15px'
    }}>
      <label style={{ fontWeight: 'bold', color: 'var(--c-2e7d32-fg)', display: 'block', marginBottom: '10px' }}>
        {t('events.fields.sync_routes')}
      </label>
      <p style={{ fontSize: '0.9em', color: 'var(--c-555555-fg)', marginTop: '0', marginBottom: '15px', lineHeight: '1.5' }}>
        {t('events.hints.sync_routes_background')}
      </p>

      <button
        type="button"
        onClick={handleSyncRoutes}
        className="btn-sync-routes"
        disabled={syncingRoutes || !formData.name.trim()}
        style={{
          padding: '8px 16px',
          backgroundColor: 'var(--c-4caf50-bg)',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: syncingRoutes || !formData.name.trim() ? 'not-allowed' : 'pointer',
          opacity: syncingRoutes || !formData.name.trim() ? 0.6 : 1
        }}
      >
        {syncingRoutes ? t('events.buttons.syncing_routes') : t('events.buttons.sync_routes')}
      </button>

      {syncSuccess && (
        <p style={{ marginTop: '10px', fontSize: '0.9em', color: 'var(--c-2e7d32-fg)', fontWeight: 'bold' }}>
          ✓ {syncSuccess}
        </p>
      )}
    </div>
  );

  // Список добавленных гонок: автор события удаляет любые, соорганизатор — только свои
  const addedRacesSection = (
    <div className="form-group">
      <div className="added-races-header">
        <label>{t('events.fields.added_races')} ({eventRoutes.length})</label>
        {/* Синхронизация идет в фоне — список обновляем по кнопке.
            Ручные гонки появляются в списке сразу, обновлять нечего */}
        {!isManualRaces && (
          <button
            type="button"
            className="added-races-refresh"
            onClick={fetchEventRoutes}
            disabled={loadingEventRoutes}
          >
            {t('events.buttons.refresh_races')}
          </button>
        )}
      </div>
      <p className="field-hint" style={{ marginTop: '4px', marginBottom: '10px' }}>
        {t(isManualRaces ? 'events.hints.manual_races_added' : 'events.hints.added_races')}
      </p>

      {isManualRaces && manualRaceAddForm}

      {loadingEventRoutes ? (
        <p className="field-hint">{t('common.loading')}</p>
      ) : eventRoutes.length === 0 ? (
        <p className="field-hint">{t('events.hints.added_races_empty')}</p>
      ) : (
        <ul className="added-races-list">
          {eventRoutes.map(race => (
            <li key={race.id} className="added-race-item">
              <div className="added-race-info">
                <span className="added-race-date">{formatRaceDate(race.start_date_time_utc)}</span>
                <span className="added-race-name">{race.name || race.route?.name || '—'}</span>
                {race.added_by && (
                  <span className="added-race-author">
                    {t('events.added_race_by')}: {organizerName(race.added_by)}
                  </span>
                )}
              </div>
              {race.can_delete && (
                <button
                  type="button"
                  className="added-race-delete"
                  onClick={() => handleDeleteRace(race.id)}
                  disabled={deletingRaceId === race.id}
                >
                  {deletingRaceId === race.id ? t('common.saving') : t('common.delete')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // Состав открытого турнира: маршруты, которые в него входят.
  // Расписания у них нет — участник едет маршрут когда захочет
  const openStagesSection = (
    <div className="form-group">
      <label>{t('events.fields.open_stages')}</label>
      <p className="field-hint" style={{ marginTop: '4px', marginBottom: '10px' }}>
        {t('events.hints.open_stages')}
      </p>

      <div className="open-stage-form">
        <input
          type="text"
          value={stageRouteUrl}
          onChange={(e) => setStageRouteUrl(e.target.value)}
          placeholder="https://riders.rouvy.com/route/12345"
        />
        <button
          type="button"
          className="btn-add-stage"
          onClick={handleAddStage}
          disabled={addingStage || !stageRouteUrl.trim()}
        >
          {addingStage ? t('events.buttons.adding_stage') : t('events.buttons.add_stage')}
        </button>
      </div>

      {loadingEventRoutes ? (
        <p className="field-hint">{t('common.loading')}</p>
      ) : eventRoutes.length === 0 ? (
        <p className="field-hint">{t('events.hints.open_stages_empty')}</p>
      ) : (
        <ul className="added-races-list">
          {eventRoutes.map(race => (
            <li key={race.id} className="added-race-item">
              <div className="added-race-info">
                <span className="added-race-name">{race.route?.name || race.name || '—'}</span>
                {race.added_by && (
                  <span className="added-race-author">
                    {t('events.added_race_by')}: {organizerName(race.added_by)}
                  </span>
                )}
              </div>
              {race.can_delete && (
                <button
                  type="button"
                  className="added-race-delete"
                  onClick={() => handleDeleteRace(race.id)}
                  disabled={deletingRaceId === race.id}
                >
                  {deletingRaceId === race.id ? t('common.saving') : t('common.delete')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // Инструкция открывается в новой вкладке, чтобы не потерять заполненную форму
  const guideLink = (
    <Link to="/events/guide" target="_blank" rel="noopener noreferrer" className="event-guide-link">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      {t('events.guide.link')}
    </Link>
  );

  if (!eventLoaded) {
    return (
      <div className={`create-edit-event-container ${isMobile ? 'mobile-view' : ''}`}>
        <div className="create-edit-event-card">
          <p className="field-hint">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Соорганизатору показываем только добавление гонок
  if (isCoOrganizerOnly) {
    return (
      <div className={`create-edit-event-container ${isMobile ? 'mobile-view' : ''}`}>
        <div className="create-edit-event-card">
          <h2>{formData.name}</h2>
          {guideLink}

          {errors.length > 0 && (
            <div className="error-messages">
              {errors.map((message, index) => (
                <p key={index} className="error-message">{message}</p>
              ))}
            </div>
          )}

          <p className="field-hint">{t('events.hints.co_organizer_access')}</p>

          {!isOfficialEvent && !isOpenTournament && !isManualRaces && routesSyncSection}

          {isOpenTournament ? openStagesSection : (!isOfficialEvent && addedRacesSection)}

          <button type="button" className="btn-cancel" onClick={() => navigate('/events')}>
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`create-edit-event-container ${isMobile ? 'mobile-view' : ''}`}>
      <div className="create-edit-event-card">
        <h2>{isEditMode ? t('events.edit_title') : t('events.create_title')}</h2>
        {guideLink}

        {errors.length > 0 && (
          <div className="error-messages">
            {errors.map((message, index) => (
              <p key={index} className="error-message">{message}</p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="event-form">
          <div className="form-group">
            <label htmlFor="name">{t('events.fields.name')} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder={t('events.placeholders.name')}
            />
          </div>

          {/* Чужие гонки синхронизацией не найти: она ищет среди проездов организаторов */}
          {!isEditMode && !isOfficialEvent && !isOpenTournament && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="manual_races"
                  checked={formData.manual_races}
                  onChange={handleInputChange}
                />
                <span>{t('events.fields.manual_races')}</span>
              </label>
              <p className="field-hint" style={{ marginTop: '4px', display: 'block' }}>{t('events.hints.manual_races')}</p>
            </div>
          )}

          {!isEditMode && isManualRaces && manualRacesCreateSection}

          {!isOfficialEvent && !isOpenTournament && !isManualRaces && (user?.admin || !isEditMode || eventOwnerId === user?.id) && (
            <div className="form-group">
              <label htmlFor="template">{t('events.fields.template')}</label>
              <input
                type="text"
                id="template"
                name="template"
                value={formData.template}
                onChange={handleInputChange}
                placeholder={t('events.placeholders.template')}
              />
              <p className="field-hint" style={{ marginTop: '4px', fontSize: '0.9em', color: 'var(--c-666666-fg)' }}>
                {t('events.hints.template')}
              </p>

              {isEditMode && routesSyncSection}
            </div>
          )}

          {isEditMode && isOpenTournament && openStagesSection}

          {isEditMode && !isOfficialEvent && !isOpenTournament && addedRacesSection}

          {/* Соорганизаторы: тоже могут добавлять гонки, их проезды Rouvy идут в событие */}
          {!isOfficialEvent && (
          <div className="form-group">
            <label htmlFor="co_organizer_search">{t('events.fields.co_organizers')}</label>
            <p className="field-hint" style={{ marginTop: '4px', marginBottom: '10px' }}>
              {t('events.hints.co_organizers')}
            </p>

            {coOrganizers.length > 0 && (
              <div className="co-organizers-list">
                {coOrganizers.map(organizer => (
                  <span key={organizer.id} className="co-organizer-chip">
                    {organizerName(organizer)}
                    <button
                      type="button"
                      className="co-organizer-remove"
                      onClick={() => removeCoOrganizer(organizer.id)}
                      title={t('common.remove')}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="co-organizer-search">
              <input
                type="text"
                id="co_organizer_search"
                value={organizerSearch}
                onChange={(e) => setOrganizerSearch(e.target.value)}
                placeholder={t('events.placeholders.co_organizers')}
                autoComplete="off"
              />

              {organizerSearch.trim().length >= 2 && (
                <div className="co-organizer-results">
                  {searchingOrganizers && (
                    <div className="co-organizer-result-empty">{t('common.loading')}</div>
                  )}
                  {!searchingOrganizers && organizerResults.length === 0 && (
                    <div className="co-organizer-result-empty">{t('events.hints.co_organizers_not_found')}</div>
                  )}
                  {organizerResults
                    .filter(candidate => candidate.id !== eventOwnerId && !coOrganizers.some(o => o.id === candidate.id))
                    .map(candidate => (
                      <button
                        type="button"
                        key={candidate.id}
                        className="co-organizer-result"
                        onClick={() => addCoOrganizer(candidate)}
                      >
                        {organizerName(candidate)}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>
          )}

          <div className="form-group">
            <label htmlFor="description">{t('events.fields.description')}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              placeholder={t('events.placeholders.description')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="event_type">{t('events.fields.type')} *</label>
            <select
              id="event_type"
              name="event_type"
              value={formData.event_type}
              onChange={handleInputChange}
              required
            >
              <option value="group_ride">{t('events.types.group_ride')}</option>
              <option value="race">{t('events.types.race')}</option>
              <option value="tournament">{t('events.types.tournament')}</option>
              {canChooseOpenTournament && (
                <option value="open_tournament">{t('events.types.open_tournament')}</option>
              )}
            </select>
            <p className="field-hint" style={{ marginTop: '8px', fontSize: '0.9em', color: 'var(--c-666666-fg)' }}>
              {formData.event_type === 'group_ride' && t('events.hints.group_ride')}
              {formData.event_type === 'race' && t('events.hints.race_single_route')}
              {formData.event_type === 'tournament' && t('events.hints.tournament_multiple_routes')}
              {isOpenTournament && t('events.hints.open_tournament')}
            </p>
          </div>

          {/* Период проведения турнира: внутри него участник закрывает все маршруты */}
          {isOpenTournament && (
            <div className="form-group">
              <label>{t('events.fields.period')}</label>
              <div className="event-period-fields">
                <label className="event-period-field">
                  <span>{t('events.fields.starts_at')}</span>
                  <input
                    type="datetime-local"
                    name="starts_at"
                    value={formData.starts_at}
                    onChange={handleInputChange}
                  />
                </label>
                <label className="event-period-field">
                  <span>{t('events.fields.ends_at')}</span>
                  <input
                    type="datetime-local"
                    name="ends_at"
                    value={formData.ends_at}
                    onChange={handleInputChange}
                  />
                </label>
              </div>
              <p className="field-hint">{t('events.hints.period')}</p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="status">{t('events.fields.status')} *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
            >
              <option value="official">{t('events.statuses.official')}</option>
              <option value="custom">{t('events.statuses.custom')}</option>
            </select>
          </div>

          <div className="form-group">
            <label>{t('events.fields.image')}</label>
            <div className="image-upload-section">
              <div className="image-preview">
                {imagePreview ? (
                  <img src={imagePreview} alt={t('a11y.eventPreview')} />
                ) : (
                  <div className="image-placeholder">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                    <p>{t('events.placeholders.no_image')}</p>
                  </div>
                )}
              </div>
              <div className="image-upload-controls">
                <label htmlFor="image-upload" className="image-upload-label">
                  {t('events.buttons.upload_image')}
                </label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                {imagePreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                      setFormData(prev => ({ ...prev, image: '' }));
                    }}
                    className="remove-image-btn"
                  >
                    {t('events.buttons.remove_image')}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="registration_closes_hours_before">{t('events.fields.registration_closes_hours_before')}</label>
            <input
              type="number"
              id="registration_closes_hours_before"
              name="registration_closes_hours_before"
              value={formData.registration_closes_hours_before || ''}
              onChange={(e) => setFormData({
                ...formData,
                registration_closes_hours_before: e.target.value ? parseInt(e.target.value) : null
              })}
              min="0"
              step="1"
              placeholder={t('events.placeholders.registration_closes_hours_before')}
            />
            <p className="field-hint">{t('events.hints.registration_closes_hours_before')}</p>
          </div>

          {/* Лимит гонок на этапе есть только там, где эти гонки заводит организатор */}
          {(formData.event_type === 'race' || formData.event_type === 'tournament') && (
            <div className="form-group">
              <label htmlFor="races_per_stage">{t('events.fields.races_per_stage')}</label>
              <input
                type="number"
                id="races_per_stage"
                name="races_per_stage"
                value={formData.races_per_stage ?? ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  races_per_stage: e.target.value !== '' ? parseInt(e.target.value) : null
                }))}
                min="1"
                step="1"
                placeholder={t('events.placeholders.races_per_stage')}
              />
              <p className="field-hint">{t('events.hints.races_per_stage')}</p>
            </div>
          )}

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="long_event"
                checked={formData.long_event}
                onChange={handleInputChange}
              />
              <span>{t('events.fields.long_event')}</span>
            </label>
            <p className="field-hint" style={{ marginTop: '4px', display: 'block' }}>{t('events.hints.long_event')}</p>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="hidden"
                checked={formData.hidden}
                onChange={handleInputChange}
              />
              <span>{t('events.fields.hidden')}</span>
            </label>
            <p className="field-hint" style={{ marginTop: '4px', display: 'block' }}>{t('events.hints.hidden')}</p>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="individual_competition"
                checked={formData.individual_competition}
                onChange={handleInputChange}
              />
              <span>{t('events.fields.individual_competition')}</span>
            </label>

            <label style={{marginLeft: '20px'}}>
              <input
                type="checkbox"
                name="team_competition"
                checked={formData.team_competition}
                onChange={handleInputChange}
              />
              <span>{t('events.fields.team_competition')}</span>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="max_scores">{t('events.fields.max_scores')}</label>
            <input
              type="number"
              id="max_scores"
              name="max_scores"
              value={formData.max_scores}
              onChange={handleInputChange}
              min="1"
              placeholder={t('events.placeholders.max_scores')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="scores_step">{t('events.fields.scores_step')}</label>
            <input
              type="number"
              id="scores_step"
              name="scores_step"
              value={formData.scores_step}
              onChange={handleInputChange}
              min="1"
              placeholder={t('events.placeholders.scores_step')}
            />
          </div>

          {formData.team_competition && (
            <div className="form-group">
              <label htmlFor="members_accounting">{t('events.fields.members_accounting')}</label>
              <input
                type="number"
                id="members_accounting"
                name="members_accounting"
                value={formData.members_accounting}
                onChange={handleInputChange}
                min="1"
                placeholder={t('events.placeholders.members_accounting')}
              />
              <p className="field-hint">{t('events.hints.members_accounting')}</p>
            </div>
          )}

          {formData.team_competition && (
            <div className="form-group">
              <label htmlFor="max_team_members">{t('events.fields.max_team_members')}</label>
              <input
                type="number"
                id="max_team_members"
                name="max_team_members"
                value={formData.max_team_members || ''}
                onChange={handleInputChange}
                min="1"
                placeholder={t('events.placeholders.max_team_members')}
              />
              <p className="field-hint">{t('events.hints.max_team_members')}</p>
            </div>
          )}

          {formData.status === 'custom' && (formData.event_type === 'race' || formData.event_type === 'tournament') && (
            <>
              <div className="form-group">
                <label htmlFor="wild_cards">{t('events.fields.wild_cards')}</label>
                <input
                  type="number"
                  id="wild_cards"
                  name="wild_cards"
                  value={formData.wild_cards ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    wild_cards: e.target.value !== '' ? parseInt(e.target.value) : null
                  }))}
                  min="0"
                  step="1"
                  placeholder={t('events.placeholders.wild_cards')}
                />
                <p className="field-hint">{t('events.hints.wild_cards')}</p>
              </div>

              {formData.wild_cards > 0 && (
              <div className="form-group">
                <label>{t('events.fields.creation_type')}</label>
                <div className="radio-options">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="creation_type"
                      value="organizer_created"
                      checked={formData.creation_type === 'organizer_created'}
                      onChange={handleInputChange}
                    />
                    <span>{t('events.creation_types.organizer_created')}</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="creation_type"
                      value="participant_created"
                      checked={formData.creation_type === 'participant_created'}
                      onChange={handleInputChange}
                    />
                    <span>{t('events.creation_types.participant_created')}</span>
                  </label>
                </div>
                <p className="field-hint">{t('events.hints.creation_type')}</p>
              </div>
              )}

              {formData.wild_cards > 0 && formData.creation_type === 'participant_created' && (
              <div className="form-group">
                <label htmlFor="wild_card_description">{t('events.fields.wild_card_description')}</label>
                <textarea
                  id="wild_card_description"
                  name="wild_card_description"
                  value={formData.wild_card_description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder={t('events.placeholders.wild_card_description')}
                />
                <p className="field-hint">{t('events.hints.wild_card_description')}</p>
              </div>
              )}
            </>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => navigate('/events')} 
              className="btn-cancel"
              disabled={loading || uploadingImage}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={loading || uploadingImage}
            >
              {loading || uploadingImage ? t('common.saving') : (isEditMode ? t('common.save') : t('common.create'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEditEvent;